// ============================================================================
// LA TVA D'UN MAGASIN — son régime et le taux qu'il donne à un article créé sans taux.
//
// POURQUOI CE FICHIER EXISTE. Le zéro de `article.vat_rate_bp` portait deux sens : « exonéré » et « pas encore
// renseigné ». Le producteur écrivait 0 pour un article que sa source ne taxait pas (« valeur d'attente »), et la
// caisse lisait ce 0 comme « inconnu » pour le vendre à 20 %. Un article réellement à 0 % était donc vendu à 20 %,
// et un magasin en franchise en base (art. 293 B du CGI), qui ne facture la TVA sur rien, ne pouvait pas exister.
//
// La réponse sépare les deux sens à la SOURCE : le zéro d'un article veut dire 0 %, toujours ; un article créé
// sans taux reçoit le taux par défaut du magasin, ÉCRIT dans sa fiche par celui qui le crée (EDI, import, écrans,
// reprise) — jamais deviné à la lecture. Et le régime de la société vit à part, comme son SIRET.
//
// ⚠️ IL VIT DANS LE CONTRAT parce que trois écrivains et deux lecteurs doivent lire la MÊME valeur de la même
//    façon : le producteur du back-office et la reprise de la Console qui écrivent le défaut, la caisse qui
//    l'applique à une ligne sans article, le descripteur des réglages qui refuse une valeur illisible.
// ============================================================================
import { VAT_RATES_BP, formatRateBp, parseRateBp } from '@orgazone/core-vat';

/** Le réglage du taux par défaut — en POUR CENT tel qu'on le tape (« 20 », « 5,5 »), jamais en points de base. */
export const VAT_DEFAULT_RATE_SETTING = 'vat.default_rate';

/**
 * Le réglage des taux PROPOSÉS dans les menus du magasin — en pour cent, séparés par « ; » (« 20;10;5,5 »).
 * ⚠️ IL RACCOURCIT LES MENUS, il n'interdit rien : un article qui porte un autre taux (repris, reçu par EDI) le garde.
 */
export const VAT_OFFERED_RATES_SETTING = 'vat.offered_rates';

/**
 * LES TAUX DU CODE GÉNÉRAL DES IMPÔTS, du plus courant au plus rare — ceux qu'un magasin peut proposer.
 * ⚠️ ILS NE SE CRÉENT PAS : la loi les fixe. Un taux ancien porté par des articles repris (19,6 %) se lit, il ne se
 *    propose plus.
 */
export const LEGAL_VAT_RATES_BP: readonly number[] = [
  VAT_RATES_BP.NORMAL, VAT_RATES_BP.INTERMEDIAIRE, VAT_RATES_BP.REDUIT, VAT_RATES_BP.PARTICULIER, VAT_RATES_BP.ZERO,
];

/** Le réglage du régime : un interrupteur, allumé = franchise en base (art. 293 B du CGI). */
export const VAT_FRANCHISE_SETTING = 'company.vat_franchise';

/** Le taux qu'un magasin qui n'a rien réglé donne à un article créé sans taux : le taux normal français. */
export const PLATFORM_DEFAULT_VAT_RATE_BP = 2000;

/**
 * LE TAUX PAR DÉFAUT DU MAGASIN, en points de base — `null` quand la valeur posée n'est pas un taux.
 *
 * ⚠️ ABSENT OU VIDE VAUT LE DÉFAUT DE LA PLATEFORME : un magasin qui n'a rien choisi n'a rien décidé.
 * ⚠️ ILLISIBLE NE VAUT RIEN : « 2O » ou « 120 » ne deviennent ni 20 % ni 100 %. L'appelant REFUSE d'écrire (le
 *    producteur, la reprise) ou de sceller (la caisse) et nomme la valeur — un taux corrigé en silence ferait
 *    collecter une TVA que personne n'a choisie. Le descripteur des réglages refuse d'ailleurs de la poser.
 */
export function defaultVatRateBp(stored: string | null | undefined): number | null {
  if (stored == null || stored.trim() === '') return PLATFORM_DEFAULT_VAT_RATE_BP;
  return parseRateBp(stored);
}

/**
 * LE TAUX D'UN ARTICLE CRÉÉ SANS TAUX — celui de sa famille, sinon celui du magasin (lu par `defaultVatRateBp`).
 * ⚠️ UNE RÈGLE POUR TOUS CEUX QUI CRÉENT : le producteur, les écrans de création qui pré-remplissent le champ, et demain la
 *    caisse. `null` : la famille n'a pas de taux et celui du magasin est illisible — l'appelant refuse, il ne devine pas.
 * ⚠️ 0 % SUR LA FAMILLE EST UN TAUX, pas une absence : seul `null` rend la main au magasin.
 */
export function articleDefaultVatRateBp(familyRateBp: number | null | undefined, storeRateBp: number | null): number | null {
  return familyRateBp ?? storeRateBp;
}

/**
 * LES TAUX PROPOSÉS PAR LE MAGASIN, en points de base, dans l'ordre de la loi — `null` quand la valeur posée est illisible.
 * ⚠️ ABSENT OU VIDE VAUT TOUS LES TAUX : un magasin qui n'a rien choisi n'a rien retiré de ses menus.
 * ⚠️ UN MORCEAU VIDE EST ILLISIBLE, pas « 0 % » : `parseRateBp` lit le vide comme zéro, et « 20;;10 » proposerait l'exonération
 *    que personne n'a choisie. Un taux hors de la loi l'est aussi.
 */
export function offeredVatRatesBp(stored: string | null | undefined): number[] | null {
  if (stored == null || stored.trim() === '') return [...LEGAL_VAT_RATES_BP];
  const picked = new Set<number>();
  for (const part of stored.split(';')) {
    if (part.trim() === '') return null;
    const bp = parseRateBp(part);
    if (bp === null || !LEGAL_VAT_RATES_BP.includes(bp)) return null;
    picked.add(bp);
  }
  return LEGAL_VAT_RATES_BP.filter((bp) => picked.has(bp));
}

/** La valeur du réglage pour ces taux — en pour cent, dans l'ordre de la loi (relue par `offeredVatRatesBp`). */
export function formatOfferedVatRates(ratesBp: readonly number[]): string {
  return LEGAL_VAT_RATES_BP.filter((bp) => ratesBp.includes(bp)).map((bp) => formatRateBp(bp, false)).join(';');
}

/**
 * CE QUE VAUT LA VALEUR D'UN RÉGLAGE DE TVA — `null` si elle peut s'écrire, sinon le motif du refus.
 *
 * ⚠️ LES RÉGLAGES DE TVA SEULEMENT, et c'est voulu : ce sont des faits FISCAUX. Un interrupteur ordinaire écrit
 *    « true » se lirait éteint sans dommage ; le régime de franchise lu éteint par erreur ferait collecter la
 *    TVA chez qui n'a pas le droit de la facturer.
 */
export function vatSettingValueProblem(name: string, value: string): string | null {
  if (name === VAT_DEFAULT_RATE_SETTING) {
    return defaultVatRateBp(value) === null ? `« ${value} » n’est pas un taux de TVA (attendu : 20, 10, 5,5… en %)` : null;
  }
  if (name === VAT_FRANCHISE_SETTING) {
    return value === '1' || value === '0' ? null : `le régime de franchise s’écrit « 1 » ou « 0 », pas « ${value} »`;
  }
  if (name === VAT_OFFERED_RATES_SETTING) {
    return offeredVatRatesBp(value) === null
      ? `« ${value} » n’est pas une liste de taux de la loi (attendu : 20;10;5,5;2,1;0, séparés par « ; »)` : null;
  }
  return null;
}
